import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { LeaderboardRow } from '../lib/rollup';
import { scoreColor, LEVEL_BAR_COLORS, LEVEL_ORDER, levelLabel, isTopMaturity } from '../lib/score';
import { RowConfetti } from './RowConfetti';

/** Compact stacked L1–L5 bar from a team's level counts. */
function MiniDistribution({ levelCounts }: { levelCounts: Record<string, number> }): React.JSX.Element {
  const total = Object.values(levelCounts).reduce((a, b) => a + b, 0);
  const levels = LEVEL_ORDER.filter((l) => levelCounts[l]);
  if (total === 0) return <span className="text-xs text-muted">—</span>;
  return (
    <div className="mini-dist">
      {levels.map((lvl) => (
        <div
          key={lvl}
          title={`${levelLabel(lvl)}: ${levelCounts[lvl]}`}
          style={{ width: `${((levelCounts[lvl] ?? 0) / total) * 100}%`, backgroundColor: LEVEL_BAR_COLORS[lvl] }}
        />
      ))}
    </div>
  );
}

/**
 * Ranked, clickable table of the aggregation units one tier below the selected
 * node (Delivery Streams under a Portfolio, Release Trains under a Stream). It
 * deliberately never ranks individual scrum teams — the page hides it at Release
 * Train scope. `title`/`unitColLabel` are tier-specific (e.g. "Release Train
 * leaderboard" / "Release Train"). The unit name links into that unit's scope,
 * matching the link-in-cell pattern of the Top 10 and standings tables.
 */
export function TeamLeaderboard({
  rows,
  title,
  unitColLabel,
  unitLabel,
  buildHref,
}: {
  rows: LeaderboardRow[];
  title: string;
  unitColLabel: string;
  unitLabel: string;
  buildHref: (unit: { number: string; name: string; tier: number }) => string;
}): React.JSX.Element {
  const firstRowRef = useRef<HTMLTableRowElement>(null);
  const top = rows[0];
  return (
    <div className="card p-md" data-testid="team-leaderboard">
      <h3 className="text-lg font-semibold mb-md">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-secondary">No {unitColLabel.toLowerCase()}s with scores yet.</p>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-muted col-rank">#</th>
                <th className="text-muted">{unitColLabel}</th>
                <th className="text-muted text-right">Avg</th>
                <th className="text-muted text-right">{unitLabel}</th>
                <th className="text-muted">Levels</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.unit.number} data-testid="leaderboard-row" ref={i === 0 ? firstRowRef : undefined}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <div className="stacked-cell">
                      <Link to={buildHref(row.unit)} className="font-medium">
                        {row.unit.name}
                      </Link>
                      {row.unit.teamCount > 1 && (
                        <span className="text-xs text-muted">{row.unit.teamCount} teams</span>
                      )}
                    </div>
                  </td>
                  <td className="text-right font-bold" style={{ color: scoreColor(row.avg) }}>{row.avg}</td>
                  <td className="text-secondary text-right">{row.count}</td>
                  <td><MiniDistribution levelCounts={row.levelCounts} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {top && isTopMaturity(top.avg) && (
            <RowConfetti rowRef={firstRowRef} refreshKey={top.unit.number} />
          )}
        </>
      )}
    </div>
  );
}
